import { Box, IconButton, Stack, Typography } from "@mui/material"
import { AuthoringAmendsContentDialog } from "authoring/routes/project/amends/components/authoring-amends-content-dialog"
import { DATE_FORMATS } from "lib/@constants/date-format"
import Iconify from "minimals-template/components/Iconify"
import { If } from "lib/@components/switch"
import { hasAmendsContent } from "authoring/routes/project/amends/lib/amends-content"
import { buildContentPreview, hasLongContent } from "authoring/routes/project/amends/lib/amends-content-preview"
import { PAYLOAD_STATUS_REJECTED } from "routes/audit/lib/audit-constants.mjs"
import { htmlToText } from "lib/html-to-text"
import { useDialog } from "lib/@hooks/useDialog"

export function AuthoringAmendsDecisionBanner({ decision, expanded = false, showReasonDetailsAction = true }) {
    const openReasonDialog = useDialog(<AuthoringAmendsContentDialog />, { maxWidth: "md", fullWidth: true })
    const status = decision?.payload?.status
    const reason = decision?.payload?.reason ?? ""
    const rejected = status === PAYLOAD_STATUS_REJECTED
    const hasReason = rejected && hasAmendsContent(reason)

    if (!status) return null

    const title = rejected ? "Change Rejected" : "Change Approved"
    const icon = rejected ? "mdi:close-circle" : "mdi:check-circle"
    const color = rejected ? "error.main" : "success.main"
    const backgroundColor = rejected ? "error.lighter" : "success.lighter"
    const reviewerName = decision?.userName ?? ""
    const dateLabel = decision?.date ? new Date(decision.date).format(DATE_FORMATS.long) : ""
    const textContent = hasReason ? htmlToText(reason, true).trim() : ""
    const previewContent = hasReason ? buildContentPreview(reason) : ""
    const isLongContent = hasReason && hasLongContent(reason)
    const showEyeIcon = !expanded && showReasonDetailsAction && isLongContent
    const displayContent = expanded ? textContent : previewContent

    return (
        <Stack spacing={0.5}>
            <If truthy={Boolean(reviewerName || dateLabel)}>
                <Stack direction="row" alignItems="center" sx={{ px: 0.25 }}>
                    <Typography variant="caption" sx={{ color: "text.secondary", flex: 1 }}>
                        {reviewerName}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary", flexShrink: 0 }}>
                        {dateLabel}
                    </Typography>
                </Stack>
            </If>
            <Box
                sx={{
                    bgcolor: backgroundColor,
                    borderRadius: 2,
                    p: 1.5,
                }}
                data-cy="authoring-amends-decision-banner"
            >
                <Stack direction="row" spacing={1} alignItems="center">
                    <Iconify icon={icon} sx={{ color }} />
                    <Typography variant="subtitle2" sx={{ color, flex: 1 }}>
                        {title}
                    </Typography>
                    <If truthy={showEyeIcon}>
                        <IconButton
                            aria-label="View decision details"
                            color="primary"
                            size="small"
                            onClick={showReasonDialog}
                            data-cy="authoring-amends-decision-banner-view-details"
                        >
                            <Iconify icon="ic:outline-remove-red-eye" />
                        </IconButton>
                    </If>
                </Stack>
                <If truthy={hasReason}>
                    <Typography
                        data-cy="authoring-amends-decision-banner-content"
                        variant="body2"
                        sx={{
                            mt: 0.75,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                        }}
                    >
                        {displayContent}
                    </Typography>
                </If>
            </Box>
        </Stack>
    )

    async function showReasonDialog() {
        await openReasonDialog({
            content: textContent,
            title,
            plainText: true,
        })
    }
}
